import { useGetAttachmentDocumentTypeList } from "@/api/services/global/master";
import { InnerBox } from "@/components/elements/InnerBox";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { MAX_FILE, MAX_SIZE, TALENT_FILE_TYPES_EXTENSION, TALENT_MINE_TYPES } from "@/features/talent/constants/file";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, orange, text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { toast } from "@/utils/toast";
import { Stack, Grid, Collapse, Typography } from "@mui/material";
import { useEffect } from "react";
import { FileRejection } from "react-dropzone/.";
import { useFormContext, useWatch } from "react-hook-form";
import { getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { TalentDropZone } from "@/features/talent/components/talent-form/TalentDropZone";
import { useCertificateNotificationCheck } from "@/features/talent/hooks/useCertificateNotificationCheck";
import { CertificateFileWarning } from "@/features/talent/components/talent-form/CertificateFileWarning";
import { DocumentTypeTagsRow, MAX_DOCUMENT_TYPES } from "@/features/talent/components/talent-form/DocumentTypeTagsRow";
import { findDuplicateResidenceCardTypeIds, DUPLICATE_RESIDENCE_CARD_TYPE_MESSAGE } from "@/utils/attachmentDocumentType";

type TalentFormCertificateFilesProps = {
  onFileChange?: () => void;
  isEditMode?: boolean;
};

export const TalentFormCertificateFiles: FCC<TalentFormCertificateFilesProps> = ({ onFileChange, isEditMode = false }) => {
  const { expanded, toggle } = useCollapsible();
  const methods = useFormContext<TTalentForm>();
  const { control, setValue } = methods;
  const { attachmentDocumentTypeOptions } = useGetAttachmentDocumentTypeList();
  const rules: TFileRules = { acceptMimeTypes: TALENT_MINE_TYPES, acceptFileTypesExtension: TALENT_FILE_TYPES_EXTENSION };

  const displayFiles = useWatch({ control, name: "displayFiles" }) || [];
  const talentUploadFiles = useWatch({ control, name: "talentUploadFiles" }) || [];
  const relatedFileTypes = useWatch({ control, name: "relatedFileTypes" }) || [];
  const companySpecificIndustryId = useWatch({ control, name: "companySpecificIndustryId" });
  // Edit mode → form has `id` (talent id); create mode → undefined. Required to route
  // existing-file OCR through the BE-cache endpoint (IROHANA_MATCH_DEV-330 follow-up).
  const talentIdFromForm = useWatch({ control, name: "id" });

  // IROHANA_MATCH_DEV-330: detect 通知書 files uploaded under 特定技能の合格書.
  const { getWarning } = useCertificateNotificationCheck(displayFiles, relatedFileTypes, companySpecificIndustryId, talentIdFromForm);

  useEffect(() => {
    const currentPaths = methods.getValues("relatedFilePaths");
    const isInitialLoad = currentPaths === undefined || currentPaths === null;

    if (talentUploadFiles.length > 0 && displayFiles.length === 0 && isInitialLoad) {
      const displayFiles: TExtendFile[] = talentUploadFiles.map(
        (file) =>
          ({
            id: file.id,
            name: file.filePath.split("/").pop() || "",
            filePath: file.filePath,
            type: file.filePath.split(".").pop() || "",
            updatedAt: file.updatedAt,
          }) as unknown as TExtendFile,
      );

      setValue("displayFiles", displayFiles);

      // Data model: 1 file ↔ N document type IDs (attachmentDocumentTypeIds: number[]).
      // Form state encodes that list as a CSV string per file, stored in `relatedFileTypes`
      // at the SAME INDEX as the file in `displayFiles`/`relatedFilePaths`.
      //   e.g. file at index 2 has type IDs [1, 7]  →  relatedFileTypes[2] = "1,7"
      // Empty list / missing → "" so array length stays aligned with files.
      const relatedFileTypesCsv: string[] = talentUploadFiles.map((file) => {
        const typeIds = file.attachmentDocumentTypeIds ?? [];
        return typeIds.map((id) => id.toString()).join(",");
      });
      setValue("relatedFileTypes", relatedFileTypesCsv);

      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);
      setValue("relatedFilePaths", existedPaths);
    } else if (isInitialLoad) {
      // Talent has no uploaded files yet (create flow or empty edit). Still register these keys as
      // empty arrays so the parent form's initial dirty snapshot (`{ ...currentValues }`) includes them.
      // Without this, parent's `Object.keys(initialSnapshot).some(...)` check skips these fields,
      // and any later upload / tag mutation goes undetected → unsaved-changes confirm modal never fires.
      setValue("displayFiles", []);
      setValue("relatedFileTypes", []);
      setValue("relatedFilePaths", []);
    }
  }, [talentUploadFiles, displayFiles.length, setValue, methods]);

  useEffect(() => {
    if (talentUploadFiles.length > 0 && displayFiles.length === 0) {
      return;
    }

    if (displayFiles.length > 0) {
      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);

      const currentPaths = methods.getValues("relatedFilePaths") || [];

      const pathsChanged =
        existedPaths.length !== currentPaths.length ||
        existedPaths.some((path, index) => path !== currentPaths[index]) ||
        currentPaths.some((path, index) => path !== existedPaths[index]);

      if (pathsChanged) {
        setValue("relatedFilePaths", existedPaths, { shouldDirty: true });
      }
    } else if (displayFiles.length === 0) {
      const currentPaths = methods.getValues("relatedFilePaths") || [];
      if (currentPaths.length > 0) {
        setValue("relatedFilePaths", [], { shouldDirty: true });
      }
    }
  }, [displayFiles, setValue, methods, talentUploadFiles.length]);

  const onChange = (value: TExtendFile[] | TExtendFile | null) => {
    // onChange fires from user actions (drop/select/clear in dropzone) → must mark form dirty
    // so consumers relying on react-hook-form's formState.isDirty (e.g. unsaved-changes confirm modal)
    // can detect the change. Without shouldDirty, only watch-based parents would notice.
    const dirty = { shouldDirty: true } as const;

    if (value === null) {
      setValue("displayFiles", [], dirty);
      setValue("relatedFiles", [], dirty);
      setValue("relatedFilePaths", [], dirty);
      setValue("relatedFileTypes", [], dirty);
      return;
    }

    const newFiles = Array.isArray(value) ? value : [value];
    const validFiles = newFiles.filter(Boolean) as TExtendFile[];

    if (validFiles.length === 0) {
      setValue("displayFiles", [], dirty);
      setValue("relatedFiles", [], dirty);
      setValue("relatedFilePaths", [], dirty);
      setValue("relatedFileTypes", [], dirty);
      return;
    }

    const currentFileTypes = methods.getValues("relatedFileTypes") || [];
    const currentDisplayFiles = methods.getValues("displayFiles") || [];
    const currentPaths = methods.getValues("relatedFilePaths") || [];

    const idToTypeMap = new Map<string, string>();
    const pathToTypeMap = new Map<string, string>();

    currentDisplayFiles.forEach((file: TExtendFile, index: number) => {
      if (file.id && currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        idToTypeMap.set(file.id, currentFileTypes[index]);
      }

      if (file.filePath && currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        pathToTypeMap.set(file.filePath, currentFileTypes[index]);
      }
    });

    currentPaths.forEach((path: string, index: number) => {
      if (currentFileTypes[index] !== undefined && currentFileTypes[index] !== null && currentFileTypes[index] !== "") {
        pathToTypeMap.set(path, currentFileTypes[index]);
      }
    });

    setValue("displayFiles", validFiles, dirty);

    const uploadFiles: TExtendFile[] = validFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles, dirty);

    const existedPaths: string[] = validFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths, dirty);

    const newFileTypes = validFiles.map((file: TExtendFile) => {
      if (file.id && idToTypeMap.has(file.id)) {
        return idToTypeMap.get(file.id) || "";
      }
      if (file.filePath && pathToTypeMap.has(file.filePath)) {
        return pathToTypeMap.get(file.filePath) || "";
      }
      return "";
    });

    setValue("relatedFileTypes", newFileTypes, dirty);

    if (onFileChange) {
      onFileChange();
    }
  };

  const handleFileRemove = (removedIndex: number, updatedFiles: TExtendFile[]) => {
    setValue("displayFiles", updatedFiles, { shouldDirty: true });

    const uploadFiles: TExtendFile[] = updatedFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles, { shouldDirty: true });

    const existedPaths: string[] = updatedFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths, { shouldDirty: true });

    const currentFileTypes = methods.getValues("relatedFileTypes") || [];
    setValue("relatedFileTypes", currentFileTypes, { shouldDirty: true, shouldValidate: true });

    if (onFileChange) {
      onFileChange();
    }

    setTimeout(() => {
      currentFileTypes.forEach((value, index) => {
        setValue(`relatedFileTypes.${index}`, value, { shouldDirty: true, shouldValidate: true });
      });

      methods.trigger("relatedFileTypes").then(() => {
        currentFileTypes.forEach((_, index) => {
          methods.trigger(`relatedFileTypes.${index}`);
        });
      });
    }, 0);
  };

  const handleFileRejections = (fileRejections: FileRejection[]) => {
    const listFileErrorSize: string[] = [];
    const listFileErrorType: string[] = [];

    fileRejections.forEach((fileReject) => {
      const file = fileReject.file;
      if (file.size > MAX_SIZE) {
        listFileErrorSize.push(file.name);
      }
      const isValid = rules.acceptMimeTypes?.some((t) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return (t === file.type || rules.acceptFileTypesExtension?.includes(ext ?? "")) ?? false;
      });
      if (!isValid) {
        listFileErrorType.push(file.name);
      }
    });

    if (listFileErrorType.length > 0) {
      const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);

      const errorType =
        `以下のファイル形式はアップロードできません。\n` +
        `${listFileErrorType.map((fileName) => `◦${fileName}`).join("\n")}\n` +
        `（アップロード可能なファイル形式は${extensions.join("、")}です。）`;

      toast.error(errorType);
      return;
    }

    if (listFileErrorSize.length > 0) {
      const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE / 1000}KB）を超えているため、アップロードできませんでした：
          ${listFileErrorSize.map((fileName) => `・${fileName}`).join("\n")}
          `;

      toast.error(errorSize);
      return;
    }
  };

  // Type mutations use WHOLE-ARRAY setValue (with a fresh array reference) instead of per-index
  // (`relatedFileTypes.${fileIndex}`). RHF's per-index setValue mutates the existing array in place;
  // because TalentForm/TalentInfoModal track dirty via a SHALLOW-copied initial snapshot
  // (`initialValuesRef.current = { ...currentValues }`), the snapshot's array shares reference with
  // the live RHF array — any in-place mutation would corrupt the snapshot and break the
  // unsaved-changes detection. Replacing the whole array with a new reference keeps the snapshot intact.
  const setRelatedFileTypeAt = (fileIndex: number, csv: string) => {
    const next = [...(methods.getValues("relatedFileTypes") || [])];
    next[fileIndex] = csv;
    setValue("relatedFileTypes", next, { shouldDirty: true });
  };

  // Adds next available document type (first option not already selected) to file's type list.
  // Prepended so the newly added tag is visible upfront (avoids being hidden behind +N overflow).
  const onAddAttachmentDocumentType = (fileIndex: number) => {
    const currentTypes = methods.getValues(`relatedFileTypes.${fileIndex}`) || "";
    const typesArray = currentTypes ? String(currentTypes).split(",").filter(Boolean) : [];
    if (typesArray.length >= MAX_DOCUMENT_TYPES) return;

    const nextType = attachmentDocumentTypeOptions.find((opt) => !typesArray.includes(String(opt.value)));
    if (!nextType) return;

    typesArray.unshift(String(nextType.value));
    setRelatedFileTypeAt(fileIndex, typesArray.join(","));
  };

  const renderDocumentTypeTags = (index: number) => {
    const currentValue = relatedFileTypes[index] || "";
    const typeIds = currentValue ? String(currentValue).split(",").filter(Boolean) : [];

    const handleTypeChange = (typeIndex: number, newValue: string) => {
      const updatedTypes = [...typeIds];
      if (newValue) {
        updatedTypes[typeIndex] = newValue;
      } else {
        updatedTypes.splice(typeIndex, 1);
      }
      setRelatedFileTypeAt(index, updatedTypes.join(","));
    };

    return (
      <DocumentTypeTagsRow
        typeIds={typeIds}
        attachmentDocumentTypeOptions={attachmentDocumentTypeOptions}
        onTypeChange={handleTypeChange}
        onAddType={() => onAddAttachmentDocumentType(index)}
      />
    );
  };

  const renderCertificateFileFooter = (index: number) => {
    const file = displayFiles[index];
    if (!file) return null;

    const currentTypes = relatedFileTypes[index] || "";
    const hasType = String(currentTypes).split(",").filter(Boolean).length > 0;
    const warning = file.id ? getWarning(file.id, index) : null;

    return (
      <>
        {!hasType && (
          <Typography variant="cap12" color={text.error} mt={1}>
            書類の種類を選択してください。
          </Typography>
        )}
        {warning && <CertificateFileWarning loading={warning.loading} msg1={warning.msg1} msg2={warning.msg2} />}
      </>
    );
  };

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="証明書など" expanded={expanded} onToggle={toggle} />
      <Collapse in={expanded}>
        <Typography variant="body2" color={text.secondary} sx={{ mt: 3 }}>
          在留カード（表・裏）、履歴書、卒業証書、特定技能合格証等、技能実習修了書等、日本語合格証、写真などをアップロードできます。上記に該当しないファイルはラベルを「その他」に設定してください。
        </Typography>
        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TalentDropZone
                options={{ multiple: true, maxSize: MAX_SIZE }}
                bordered={true}
                rules={{
                  maxFile: MAX_FILE,
                  maxSize: MAX_SIZE,
                  acceptFileTypesExtension: rules.acceptFileTypesExtension,
                  acceptMimeTypes: rules.acceptMimeTypes,
                }}
                enableFileList={false}
                enableFileListCustom={true}
                twoColumnLayout={true}
                onChange={onChange}
                onFileRemove={handleFileRemove}
                handleFileRejections={handleFileRejections}
                nonControl
                value={methods.getValues("displayFiles")}
                isEditMode={isEditMode}
                childrenCustom={renderDocumentTypeTags}
                footerCustom={renderCertificateFileFooter}
              />
            </Grid>
          </Grid>
          {/* Cross-file: each residence card side (表/裏) may be tagged on at most one file.
              Non-blocking orange warning — does NOT prevent saving. */}
          {findDuplicateResidenceCardTypeIds(relatedFileTypes).length > 0 && (
            <Typography variant="cap12" color={orange[80]} mt={1}>
              {DUPLICATE_RESIDENCE_CARD_TYPE_MESSAGE}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </InnerBox>
  );
};
